import React from "react";
import { PageHeader, Card } from "@/components/UIKit";
import { CheckCircle2, AlertTriangle, XCircle, Zap, ExternalLink } from "lucide-react";

const roleTable = [
  { email: "admin@krafthub.no", role: "Superadmin", access: "ALT — hele plattformen" },
  { email: "kundeservice@krafthub.no", role: "Kundeservice", access: "Kunder, målepunkter, saker, opprette fakturaer" },
  { email: "okonomi@krafthub.no", role: "Økonomi", access: "Fakturering, betaling, inkasso, regnskapseksport" },
  { email: "drift@krafthub.no", role: "Drift", access: "Målepunkter, avtaler, Elhub-operasjoner" },
  { email: "portal@bedrift.no", role: "Bedriftsbruker", access: "KUN sin egen bedrift via /portal" },
];

const apiTable = [
  { name: "Elhub", need: "BRS-avtale + PKCS12-sertifikat + client_id/secret + Party ID (GLN)", where: "elhub.no/leverandor" },
  { name: "Nord Pool", need: "API-abonnement", where: "data.nordpoolgroup.com" },
  { name: "Resend", need: "API-nøkkel (fra €0/mnd)", where: "resend.com" },
  { name: "Twilio", need: "Account SID + norsk sender-nummer", where: "twilio.com" },
  { name: "PEPPOL/EHF", need: "PEPPOL-adresse + sertifikat (via Sendregning/Unimicro)", where: "Registrering" },
  { name: "Tripletex/Fiken", need: "API-tokens fra egen konto", where: "Ditt regnskapssystem" },
];

export default function Status() {
  return (
    <div data-testid="status-page">
      <PageHeader title="Produksjonsstatus" subtitle="Er systemet klart? Hvem kan logge inn? Hva mangler?" />

      <Card className="p-5 mb-4 border-emerald-500/20">
        <div className="flex items-start gap-3">
          <div className="h-8 w-8 rounded-md bg-emerald-500/10 border border-emerald-500/30 grid place-items-center shrink-0">
            <Zap className="h-4 w-4 text-emerald-400" />
          </div>
          <div>
            <div className="font-heading text-lg font-semibold text-zinc-50 mb-1">Er systemet klart?</div>
            <div className="text-sm text-zinc-300 leading-relaxed">
              <span className="text-emerald-400 font-medium">Ja teknisk sett — nei, ikke i produksjon.</span> All logikk (auth, kryptering, prismotor, PDF, KID, MVA, elavgift, strømstøtte, RBAC, audit-logg) er 100 % ekte og produksjonsklar. Men systemet kjører i Emergent sitt preview-miljø med demo-data. Må deployes til norsk sky (Azure/GCP Norge for GDPR) for reell drift, og de 24 seedede kundene må slettes før første ekte kunde.
            </div>
          </div>
        </div>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
        <Card className="p-5">
          <div className="font-heading font-medium text-zinc-100 mb-3">1. Hvem kan logge inn nå?</div>
          <div className="text-xs text-zinc-500 mb-3">Passord for alle: <code className="bg-zinc-900 px-1.5 py-0.5 rounded text-emerald-400">Krafthub2026!</code></div>
          <div className="space-y-2">
            {roleTable.map((r) => (
              <div key={r.email} className="border border-zinc-900 rounded p-3 bg-zinc-950/40">
                <div className="flex items-center justify-between mb-1">
                  <span className="font-mono text-xs text-emerald-400">{r.email}</span>
                  <span className="text-[10px] uppercase tracking-wider bg-zinc-800 text-zinc-300 px-1.5 py-0.5 rounded">{r.role}</span>
                </div>
                <div className="text-xs text-zinc-400">{r.access}</div>
              </div>
            ))}
          </div>
        </Card>

        <Card className="p-5">
          <div className="font-heading font-medium text-zinc-100 mb-3">2. Bedriftskunder og portalen</div>
          <div className="text-sm text-zinc-300 mb-3"><span className="text-emerald-400 font-medium">Ja, det fungerer.</span> Slik gir du en bedriftskunde tilgang:</div>
          <ol className="text-sm text-zinc-300 space-y-1.5 list-decimal list-inside mb-4">
            <li>Kunden må finnes i systemet (opprett via <span className="text-emerald-400">/kunder/ny</span>)</li>
            <li>Gå til <span className="text-emerald-400">Innstillinger → Portal-invitasjoner</span></li>
            <li>Velg bedrift, navn + e-post → send</li>
            <li>De får engangslenke gyldig 7 dager</li>
            <li>De setter passord og får rollen <span className="text-emerald-400">bedriftsbruker</span></li>
          </ol>
          <div className="border-t border-zinc-900 pt-3">
            <div className="text-xs uppercase tracking-wider text-zinc-500 mb-2">Hva de ser i portalen</div>
            <div className="space-y-1 text-xs">
              <div className="flex items-center gap-1.5 text-emerald-400"><CheckCircle2 className="h-3 w-3" /> Dashboard med KPI</div>
              <div className="flex items-center gap-1.5 text-emerald-400"><CheckCircle2 className="h-3 w-3" /> Forbrukskurve</div>
              <div className="flex items-center gap-1.5 text-emerald-400"><CheckCircle2 className="h-3 w-3" /> Egne fakturaer + PDF-nedlasting</div>
              <div className="flex items-center gap-1.5 text-emerald-400"><CheckCircle2 className="h-3 w-3" /> Målepunkter og avtaler</div>
              <div className="flex items-center gap-1.5 text-emerald-400"><CheckCircle2 className="h-3 w-3" /> Sende inn saker</div>
              <div className="flex items-center gap-1.5 text-red-400 mt-2"><XCircle className="h-3 w-3" /> IKKE andre kunder eller admin-data</div>
            </div>
          </div>
        </Card>
      </div>

      <Card className="p-5 mb-4">
        <div className="font-heading font-medium text-zinc-100 mb-2">3. Kan jeg selge tilgang til andre kraftselskaper?</div>
        <div className="text-sm text-zinc-300 mb-3">
          <span className="text-emerald-400 font-medium">Ja — multi-tenant er allerede bygget inn.</span> All data er tagget med <code className="bg-zinc-900 px-1 rounded text-xs">tenant_id</code> og isolert.
          Men det mangler ett siste stykke: <span className="text-amber-400">tenant-opprettelse i UI</span>. I dag er kun én tenant (Demo Kraft AS) opprettet via seed-scriptet.
        </div>
        <div className="text-xs text-zinc-400 leading-relaxed">
          For å slippe inn nye kraftselskaper som betalende kunder må vi bygge en superadmin-side som lar deg opprette ny tenant (navn, orgnr, første admin-e-post + passord) og eventuelt koble på Stripe-abonnement. Estimert 2 timers jobb.
        </div>
      </Card>

      <Card className="p-5 mb-4">
        <div className="font-heading font-medium text-zinc-100 mb-3">4. Hva trengs for reell produksjon?</div>

        <div className="text-xs uppercase tracking-wider text-zinc-500 mb-2">Teknisk infrastruktur</div>
        <ul className="text-sm text-zinc-300 space-y-1 mb-4 list-disc list-inside">
          <li><span className="text-zinc-100">Domene</span>: kjøp <code className="bg-zinc-900 px-1 rounded text-xs">krafthub.no</code></li>
          <li><span className="text-zinc-100">Deployment</span>: Azure Norge / GCP Europe-North1 (Trondheim) for GDPR</li>
          <li><span className="text-zinc-100">Database</span>: MongoDB Atlas M10+ med daglig backup i EU-region</li>
          <li><span className="text-zinc-100">E-post-domene</span>: verifisere DNS for faktura-avsender</li>
        </ul>

        <div className="text-xs uppercase tracking-wider text-zinc-500 mb-2">Ekte API-tilgang (venter på deg)</div>
        <div className="overflow-hidden border border-zinc-900 rounded-md">
          <table className="w-full text-sm">
            <thead className="bg-zinc-950/60 text-[11px] uppercase text-zinc-500">
              <tr>
                <th className="py-2 px-3 text-left font-medium">Integrasjon</th>
                <th className="py-2 px-3 text-left font-medium">Hva du må skaffe</th>
                <th className="py-2 px-3 text-left font-medium">Hvor</th>
              </tr>
            </thead>
            <tbody>
              {apiTable.map((a) => (
                <tr key={a.name} className="border-t border-zinc-900">
                  <td className="py-2 px-3 font-medium text-emerald-400">{a.name}</td>
                  <td className="py-2 px-3 text-zinc-300 text-xs">{a.need}</td>
                  <td className="py-2 px-3 text-zinc-500 text-xs">{a.where}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="text-xs uppercase tracking-wider text-zinc-500 mt-4 mb-2">Juridisk / GDPR</div>
        <ul className="text-sm text-zinc-300 space-y-1 list-disc list-inside">
          <li>Databehandleravtale med hver kraftleverandør</li>
          <li>Personvernerklæring</li>
          <li>Retention-policy (hvor lenge lagrer du fnr, målingsdata)</li>
        </ul>
      </Card>

      <Card className="p-5">
        <div className="font-heading font-medium text-zinc-100 mb-3">Oppsummert</div>
        <div className="space-y-1.5 text-sm">
          <div className="flex items-center gap-2 text-emerald-400"><CheckCircle2 className="h-4 w-4" /> Kode og logikk: produksjonsklar</div>
          <div className="flex items-center gap-2 text-emerald-400"><CheckCircle2 className="h-4 w-4" /> Multi-tenant: bygget inn (mangler UI for å opprette nye)</div>
          <div className="flex items-center gap-2 text-emerald-400"><CheckCircle2 className="h-4 w-4" /> Portal: fungerer, kan invitere bedriftskunder nå</div>
          <div className="flex items-center gap-2 text-amber-400"><AlertTriangle className="h-4 w-4" /> Eksterne integrasjoner: kjører sandbox til du gir ekte credentials</div>
          <div className="flex items-center gap-2 text-amber-400"><AlertTriangle className="h-4 w-4" /> Deployment: må flyttes fra preview til norsk sky</div>
          <div className="flex items-center gap-2 text-amber-400"><AlertTriangle className="h-4 w-4" /> Testdata: må slettes før første ekte kunde</div>
        </div>
      </Card>
    </div>
  );
}
